import { NUSTARTZ_LOGO } from "../constants/imageConstants";
import { Link } from "react-router-dom";
import { useState } from "react";
import emailjs from "@emailjs/browser";
import { ArrowRight } from "lucide-react";

// EmailJS configuration
const publicAPIkey = "SDrjmF9ADy2P9sabf";
const serviceID = "service_fl70gf2";
const templateID = "template_25syksp";

export default function Footer() {
  const isActive = (path: string) => location.pathname === path;
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Email validation
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(email)) {
      setMessage("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    const emailParams = {
      firstName: "Newsletter",
      lastName: "Subscriber",
      email: email,
      phoneNumber: "-",
      message: "Newsletter subscription",
    };

    try {
      await emailjs.send(serviceID, templateID, emailParams, publicAPIkey);
      setMessage("Successfully subscribed to newsletter!");
      setEmail("");
    } catch (error) {
      console.error("Failed to send newsletter subscription:", error);
      setMessage("Failed to subscribe. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <footer className="relative px-5 sm:px-10 lg:px-16 py-12 bg-transparent">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* 1) Brand */}
        <section className="flex flex-col items-start space-y-6">
          <img src={NUSTARTZ_LOGO} alt="NuStartz" className="h-10 w-auto" />
          <p className="text-lg lg:text-xl text-[#d9d9d9] leading-relaxed">
            We help businesses at every stage move faster, scale smarter, and
            build with confidence. From MVPs to enterprise systems, our teams
            deliver agile, outcome-driven execution aligned with your growth
            goals.
          </p>
        </section>

        {/* 2) Navigation */}
        <nav className="flex flex-col items-start space-y-4">
          <h3 className="text-xl text-white font-medium mb-2">Quick Links</h3>
          <ul className="flex flex-col gap-4 text-lg">
            {[
              { to: "/", label: "Home" },
              { to: "/services", label: "Services" },
              { to: "/insights", label: "Insights" },
              { to: "/about-us", label: "About Us" },
            ].map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className={`tracking-wide transition-colors ${
                    isActive(link.to)
                      ? "text-white"
                      : "text-gray-100 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* 3) Newsletter + Contact + Social */}
        <div className="flex flex-col gap-6">
          {/* Newsletter */}
          <div>
            <p className="text-xl lg:text-xl text-white font-medium py-1">
              Newsletter Subscription
            </p>
            <form onSubmit={handleNewsletterSubmit} className="mt-3">
              <label htmlFor="footer-email" className="sr-only">
                Email
              </label>
              <div className="relative">
                <input
                  id="footer-email"
                  type="email"
                  placeholder="Enter your email..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 pr-12 bg-transparent border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  disabled={isSubmitting || !email}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-full transition-colors duration-200"
                >
                  <ArrowRight 
                    size={16} 
                    className="text-white" 
                  />
                </button>
              </div>
              {message && (
                <p className={`mt-2 text-sm ${
                  message.includes("Successfully") 
                    ? "text-green-400" 
                    : "text-red-400"
                }`}>
                  {message}
                </p>
              )}
            </form>
          </div>

          {/* Contact */}
          <div className="text-lg lg:text-xl text-[#d9d9d9] space-y-1">
            <p>Website - www.nustartz.com</p>
            <p>Email - contact@nustartz.com</p>
            <p>Mobile - (+1) 310-818-3415</p>
          </div>

          {/* Social */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <a href="https://www.linkedin.com/company/nustartz/posts/?feedView=all" target="_blank" rel="noopener noreferrer"> 
              <img
                src="/assets/Footer/linkedIn.png"
                alt="LinkedIn"
                className="w-full h-8 hover:scale-125 transition-all duration-300 cursor-pointer"
              /> 
              </a>
              <a href="https://www.instagram.com/nustartz/" target="_blank" rel="noopener noreferrer"> 
              <img
                src="/assets/Home Page/PNG/Vector-1.png"
                alt="Instagram"
                className="w-full h-8 hover:scale-125 transition-all duration-300 cursor-pointer"
              />
              </a>
              <a href="https://www.youtube.com/@NuStartz" target="_blank" rel="noopener noreferrer"> 
              <img
                src="/assets/Home Page/PNG/Mask group.png"
                alt="Youtube"
                className="w-full h-8 hover:scale-125 transition-all duration-300 cursor-pointer"
              />
              </a>
              <a href="https://x.com/NuStartz" target="_blank" rel="noopener noreferrer"> 
              <img
                src="/assets/Home Page/PNG/devicon_twitter.png"
                alt="Twitter"
                className="w-full h-8 hover:scale-125 transition-all duration-300 cursor-pointer"
              />
              </a>
              <a href="https://www.facebook.com/share/19xETUcAsU/" target="_blank" rel="noopener noreferrer"> 
              <img
                src="/assets/Home Page/PNG/Vector.png"
                alt="Facebook"
                className="w-full h-8 hover:scale-125 transition-all duration-300 cursor-pointer"
              />
              </a>
            </div>
            <p className="text-lg lg:text-xl text-[#d9d9d9]">@nustartz</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
